import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarClock } from 'lucide-react';
import apiService from '../services/apiService';
import WeekGrid from '../components/Schedule/WeekGrid';
import { theme, rgba } from '../theme';

function shiftWeek(weekStart, deltaDays) {
  const d = new Date(`${weekStart}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

// Reachable by EVERY authenticated user, unlike AIMPage - the real view-scope/edit-
// permission split is resolved server-side (GET /api/schedule/defaults) and re-enforced
// on every write; this page just renders whatever that resolves to.
export default function SchedulePage() {
  const [access, setAccess] = useState(null); // { viewScope, canEdit, ... }
  const [weekStart, setWeekStart] = useState(null);
  const [view, setView] = useState(null); // { weekStart, weekEnd, shifts, drift, clients, canEdit }
  const [loading, setLoading] = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiService.getScheduleDefaults();
        setAccess(data);
      } catch (err) {
        setError(err.message || 'Failed to load schedule access.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadWeek = useCallback(async (ws) => {
    setWeekLoading(true);
    setError(null);
    try {
      const data = await apiService.getScheduleView(ws || undefined);
      setView(data);
      setWeekStart(data.weekStart);
    } catch (err) {
      setError(err.message || 'Failed to load schedule.');
    } finally {
      setWeekLoading(false);
    }
  }, []);

  useEffect(() => { if (access) loadWeek(); }, [access, loadWeek]);

  if (loading) {
    return <div style={styles.page}><p style={{ color: '#94a3b8' }}>Loading...</p></div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><CalendarClock size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />Schedule</h1>
          <p style={styles.sub}>
            {access?.viewScope === 'own' && 'Your shifts'}
            {access?.viewScope === 'site' && 'Your site\'s shifts'}
            {access?.viewScope === 'company' && 'Company-wide shifts'}
            {access?.canEdit === false ? ' (view only)' : ''}
          </p>
        </div>
        {view && (
          <div style={styles.weekNav}>
            <button onClick={() => loadWeek(shiftWeek(weekStart, -7))} style={styles.navBtn} title="Previous week"><ChevronLeft size={16} /></button>
            <span style={styles.weekLabel}>{view.weekStart} – {view.weekEnd}</span>
            <button onClick={() => loadWeek(shiftWeek(weekStart, 7))} style={styles.navBtn} title="Next week"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {weekLoading || !view ? (
        <p style={{ color: '#94a3b8' }}>Loading...</p>
      ) : (
        <WeekGrid
          weekStart={view.weekStart}
          weekEnd={view.weekEnd}
          shifts={view.shifts}
          drift={view.drift}
          clients={view.clients}
          roster={view.roster}
          canEdit={view.canEdit}
          onRefresh={() => loadWeek(weekStart)}
        />
      )}
    </div>
  );
}

const styles = {
  page:   { padding: '32px', minHeight: '100vh', background: `linear-gradient(135deg,${rgba(theme.colors.primaryRgb, 0.05)},${rgba(theme.colors.accentRgb, 0.05)})` },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: 12 },
  title:  { color: '#e2e8f0', fontSize: '28px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center' },
  sub:    { color: theme.colors.textOnPageBackground, fontSize: '14px', marginTop: '4px' },
  weekNav: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: '6px 10px' },
  navBtn: { background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', padding: 4 },
  weekLabel: { color: '#e2e8f0', fontSize: 13, fontWeight: 600 },
  errorBox: { padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', marginBottom: 16 },
};
